import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { osintApi, type SignalQueryParams, type SuggestionPromoteRequest } from '../api/osint';

export function useOsintSignals(params: SignalQueryParams = {}) {
  return useQuery({
    queryKey: ['osint', 'signals', params],
    queryFn: ({ signal }) => osintApi.getSignals(params, signal),
    refetchInterval: 30_000,
    staleTime: 15_000,
  });
}

/**
 * @param options.pollInterval — Override the default 20s poll cadence.
 *   Pass `false` to stop polling (cached data is still returned).
 */
export function useOsintHealth(options?: { pollInterval?: number | false }) {
  return useQuery({
    queryKey: ['osint', 'health'],
    queryFn: () => osintApi.getHealth(),
    refetchInterval: options?.pollInterval ?? 20_000,
    staleTime: 10_000,
  });
}

/**
 * @param options.pollInterval — Override the default 30s poll cadence.
 *   Pass `false` to stop polling (cached data is still returned).
 */
export function useOsintSummary(options?: { pollInterval?: number | false }) {
  return useQuery({
    queryKey: ['osint', 'summary'],
    queryFn: () => osintApi.getSummary(),
    refetchInterval: options?.pollInterval ?? 30_000,
    staleTime: 15_000,
  });
}

/**
 * @param options.pollInterval — Override the default 60s poll cadence.
 *   Pass `false` to stop polling (cached data is still returned).
 */
export function useSuggestions(limit = 15, options?: { pollInterval?: number | false }) {
  return useQuery({
    queryKey: ['osint', 'suggestions', limit],
    queryFn: () => osintApi.getSuggestions(limit),
    refetchInterval: options?.pollInterval ?? 60_000,
    staleTime: 30_000,
  });
}

/**
 * Batch-fetch OSINT signals by their IDs from the database.
 * Used by the cluster preview desk to load actual triggering signal records
 * that may have aged out of the in-memory live feed.
 *
 * Returns null when signalIds is empty or null (disabled query).
 */
export function useSignalsByIds(signalIds: string[] | null) {
  // Cap at 500 IDs client-side to match backend hard cap
  const capped = signalIds && signalIds.length > 0 ? signalIds.slice(0, 500) : null;
  const idsParam = capped ? capped.join(',') : null;
  return useQuery({
    queryKey: ['osint', 'signals-by-ids', idsParam],
    // Backend auto-raises limit when ids param is present — no explicit limit needed
    queryFn: ({ signal }) => osintApi.getSignals({ ids: idsParam! }, signal),
    enabled: !!idsParam,
    staleTime: 60_000,
  });
}

export function usePromoteSuggestion() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (body: SuggestionPromoteRequest) => osintApi.promoteSuggestion(body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['investigations'] });
      queryClient.invalidateQueries({ queryKey: ['osint', 'suggestions'] });
      queryClient.invalidateQueries({ queryKey: ['osint', 'summary'] });
    },
  });
}
